import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  Conversation,
  ConversationMember,
  Message,
  MessageReadBy,
  NotificationLevel,
  Poll,
  Reaction,
  User,
} from '@/lib/api';
import type { WsStatus } from '@/lib/ws';

interface TypingState {
  userIds: Set<string>;
  expiresAt: number; // ms epoch; entries considered stale past this
}

interface ChatState {
  conversations: Conversation[];
  currentConversationId: string | null;
  currentUserId: string | null;
  messagesByConversation: Map<string, Message[]>;
  // Messages received over WS while the REST GET/messages was in flight.
  // When the REST GET returns, the pending set is folded into the canonical
  // list so we don't clobber a message we just received.
  wsPendingMessages: Map<string, Map<string, Message>>;
  membersByConversation: Map<string, ConversationMember[]>;
  typingByConversation: Map<string, TypingState>;
  presenceByUser: Map<string, { online: boolean; lastSeen: string | null }>;
  pollsById: Map<string, Poll>;
  // Pending read markers per conversation so the mark-read debounce knows
  // what to commit. Cleared on conversation switch. The value carries both
  // the message id (sent to the backend) and its createdAt (used to decide
  // whether a NEW marker is older or newer than the pending one — message
  // ids are random hex, not time-ordered, so they can't be compared).
  pendingReadMarkers: Map<string, { id: string; createdAt: string }>;

  // F-001 — WS connection status, mirrored from ConversationSocket so any
  // component can read it without subscribing to the socket directly.
  wsStatus: WsStatus;
  // F-007 — reactions keyed by messageId. Populated lazily by the message
  // detail fetch and kept in sync by WS `reaction_update` events.
  reactionsByMessage: Map<string, Reaction[]>;
  // F-006 — per-conversation notification level. Default 'all' is implicit
  // (not stored server-side unless the user changes it), so the lookup
  // helper returns 'all' when the key is missing.
  notificationPrefs: Map<string, NotificationLevel>;
  // F-010 — saved composer drafts, keyed by conversationId. Persistence is
  // wired in the components/UI layer via a small localStorage adapter; the
  // store just owns the in-memory slice.
  draftsByConversation: Record<string, { content: string; replyToId: string | null }>;
  // F-009 — read receipts, keyed by messageId. Populated lazily by the
  // message-detail endpoint (`Messages.readBy`) when the user opens the
  // receipt popover.
  readByByMessage: Map<string, MessageReadBy[]>;

  // Actions
  setConversations: (convs: Conversation[]) => void;
  upsertConversation: (conv: Conversation) => void;
  setCurrentConversation: (id: string | null) => void;
  setCurrentUserId: (id: string | null) => void;
  setMessages: (conversationId: string, messages: Message[], replace?: boolean) => void;
  upsertMessage: (conversationId: string, msg: Message) => void;
  removeMessage: (conversationId: string, messageId: string, deletedAtIso?: string) => void;
  setMembers: (conversationId: string, members: ConversationMember[]) => void;
  upsertMember: (conversationId: string, member: ConversationMember) => void;
  removeMember: (conversationId: string, userId: string) => void;
  setTyping: (conversationId: string, userId: string, isTyping: boolean) => void;
  cleanTyping: () => void;
  setPresence: (userId: string, online: boolean, lastSeen?: string | null) => void;
  upsertPoll: (poll: Poll) => void;
  queueReadMarker: (conversationId: string, messageId: string, createdAt: string) => string | null;
  consumeReadMarker: (conversationId: string) => string | null;
  setWsStatus: (status: WsStatus) => void;
  setReactions: (messageId: string, reactions: Reaction[]) => void;
  setNotificationPref: (conversationId: string, level: NotificationLevel) => void;
  getConversationPref: (conversationId: string) => NotificationLevel;
  setDraft: (conversationId: string, content: string, replyToId: string | null) => void;
  clearDraft: (conversationId: string) => void;
  setReadBy: (messageId: string, readBy: MessageReadBy[]) => void;
  reset: () => void;
}

const TYPING_TTL_MS = 6_000;

export const useChatStore = create<ChatState>()(
  subscribeWithSelector((set, get) => ({
  conversations: [],
  currentConversationId: null,
  currentUserId: null,
  messagesByConversation: new Map(),
  wsPendingMessages: new Map(),
  membersByConversation: new Map(),
  typingByConversation: new Map(),
  presenceByUser: new Map(),
  pollsById: new Map(),
  pendingReadMarkers: new Map(),
  wsStatus: 'idle',
  reactionsByMessage: new Map(),
  notificationPrefs: new Map(),
  draftsByConversation: {},
  readByByMessage: new Map(),

  setConversations: (convs) => set({ conversations: convs }),

  upsertConversation: (conv) =>
    set((s) => {
      const idx = s.conversations.findIndex((c) => c.id === conv.id);
      const next = s.conversations.slice();
      if (idx === -1) {
        next.unshift(conv);
      } else {
        // Preserve previously-known `members` and `lastMessage` if the
        // incoming payload omits them (e.g. the detail endpoint does not
        // embed members on the conversation row, so spreading would
        // otherwise clobber the sidebar's member info with undefined).
        //
        // Note the adapter (`coerceConversation`) ALWAYS writes both fields —
        // `members: undefined` and `lastMessage: null` — so "omitted" has to
        // be detected by value, not by key presence.
        const prev = next[idx];
        const merged = { ...prev, ...conv };
        if (!Array.isArray(conv.members) && prev.members) {
          merged.members = prev.members;
        }
        if (conv.lastMessage == null && prev.lastMessage) {
          merged.lastMessage = prev.lastMessage;
        }
        next[idx] = merged;
      }
      return { conversations: next };
    }),

  setCurrentConversation: (id) => set({ currentConversationId: id }),
  setCurrentUserId: (id) => set({ currentUserId: id }),

  setMessages: (conversationId, messages, replace = true) =>
    set((s) => {
      const map = new Map(s.messagesByConversation);
      if (replace) {
        // Merge with any messages the WS delivered between the time the
        // REST GET was issued and the response arrived. Without this, a
        // fresh message delivered over WS could be clobbered when the
        // REST GET returns a snapshot that doesn't yet include it.
        const wsPending = s.wsPendingMessages.get(conversationId);
        if (wsPending && wsPending.size > 0) {
          const ids = new Set(messages.map((m) => m.id));
          const pending = Array.from(wsPending.values()).filter((m) => !ids.has(m.id));
          if (pending.length > 0) {
            messages = [...messages, ...pending];
          }
          // Pending is now folded into the canonical list — clear it.
          const newPending = new Map(s.wsPendingMessages);
          newPending.delete(conversationId);
          return { messagesByConversation: map.set(conversationId, messages), wsPendingMessages: newPending };
        }
        map.set(conversationId, messages);
      } else {
        const existing = map.get(conversationId) ?? [];
        map.set(conversationId, [...existing, ...messages]);
      }
      return { messagesByConversation: map };
    }),

  upsertMessage: (conversationId, msg) =>
    set((s) => {
      const map = new Map(s.messagesByConversation);
      const list = map.get(conversationId) ?? [];
      const idx = list.findIndex((m) => m.id === msg.id);
      let nextList: Message[];
      if (idx === -1) nextList = [...list, msg];
      else {
        nextList = list.slice();
        nextList[idx] = { ...nextList[idx], ...msg };
      }
      // Sort numerically on createdAt (ms since epoch) so we're not
      // relying on ISO strings being fixed-width Z-suffixed UTC. The id
      // tie-breaker stays as a locale-compare since ids are fixed-width
      // hex and the comparison is for deterministic ordering only.
      nextList.sort((a, b) => {
        const ta = Date.parse(a.createdAt);
        const tb = Date.parse(b.createdAt);
        const t = (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
        return t !== 0 ? t : a.id.localeCompare(b.id);
      });
      map.set(conversationId, nextList);

      // Defensive merge: when a message update arrives WITHOUT explicit
      // reactions/readBy fields, the spread above already preserves any
      // fields the existing row had. Reactions and read-by live in their
      // own maps keyed by messageId — we only touch them if the caller
      // passed them through (currently no caller does, but a future
      // REST list that embeds reactions would route through here). The
      // explicit `=== undefined` guard prevents accidentally blanking an
      // existing entry when the field is absent from the payload.
      const reactions = (msg as Message & { reactions?: Reaction[] }).reactions;
      const readBy = (msg as Message & { readBy?: MessageReadBy[] }).readBy;
      const reactionsByMessage =
        reactions !== undefined ? new Map(s.reactionsByMessage).set(msg.id, reactions) : s.reactionsByMessage;
      const readByByMessage =
        readBy !== undefined ? new Map(s.readByByMessage).set(msg.id, readBy) : s.readByByMessage;

      // If the conversation doesn't yet have a canonical list (i.e. the
      // REST GET/messages is still in flight), stash this message so it
      // gets folded in when the GET returns. This prevents the WS message
      // from being clobbered by an older REST snapshot.
      if (!s.messagesByConversation.has(conversationId) || list.length === 0) {
        const pending = new Map(s.wsPendingMessages);
        const inner = new Map(pending.get(conversationId) ?? []);
        inner.set(msg.id, msg);
        pending.set(conversationId, inner);
        return {
          messagesByConversation: map,
          wsPendingMessages: pending,
          reactionsByMessage,
          readByByMessage,
          conversations: s.conversations,
        };
      }

      // Also update the conversation's lastMessage preview if newer, AND
      // re-sort the sidebar so the most recently active conversation
      // bubbles to the top (the backend's list query is ORDER BY
      // updated_at DESC, but a WS upsert updates updatedAt without
      // re-sorting).
      const convs = s.conversations.slice();
      const cIdx = convs.findIndex((c) => c.id === conversationId);
      if (cIdx !== -1) {
        const last = convs[cIdx].lastMessage;
        if (!last || last.id === msg.id || msg.createdAt >= last.createdAt) {
          convs[cIdx] = { ...convs[cIdx], lastMessage: msg, updatedAt: msg.createdAt };
        }
        // Splice the touched conversation to the front of the list — its
        // updatedAt just changed, so it's the new "most recent". Skip if
        // it's already at the top to avoid a needless re-render.
        if (cIdx !== 0) {
          const [touched] = convs.splice(cIdx, 1);
          convs.unshift(touched);
        }
      }
      return {
        messagesByConversation: map,
        reactionsByMessage,
        readByByMessage,
        conversations: convs,
      };
    }),

  removeMessage: (conversationId, messageId, deletedAtIso?) =>
    set((s) => {
      const map = new Map(s.messagesByConversation);
      const list = map.get(conversationId);
      if (!list) return s;
      // If the caller doesn't have a server-stamped deletedAt (e.g. WS
      // message_delete broadcasts without one), leave it null rather
      // than stamping the client clock — a clock-skewed client would
      // produce a deletedAt the server later disagrees with, and the
      // renderer's "deleted" copy will still show.
      const deletedAt = deletedAtIso ?? new Date().toISOString();
      const filtered = list.map((m) =>
        m.id === messageId ? { ...m, content: '', deletedAt } : m,
      );
      map.set(conversationId, filtered);
      return { messagesByConversation: map };
    }),

  setMembers: (conversationId, members) =>
    set((s) => {
      const map = new Map(s.membersByConversation);
      map.set(conversationId, members);
      return { membersByConversation: map };
    }),

  upsertMember: (conversationId, member) =>
    set((s) => {
      const map = new Map(s.membersByConversation);
      const list = map.get(conversationId) ?? [];
      const idx = list.findIndex((m) => m.userId === member.userId);
      const next = idx === -1 ? [...list, member] : list.map((m, i) => (i === idx ? member : m));
      map.set(conversationId, next);
      return { membersByConversation: map };
    }),

  removeMember: (conversationId, userId) =>
    set((s) => {
      const map = new Map(s.membersByConversation);
      const list = map.get(conversationId);
      if (!list) return s;
      map.set(
        conversationId,
        list.filter((m) => m.userId !== userId),
      );
      return { membersByConversation: map };
    }),

  setTyping: (conversationId, userId, isTyping) =>
    set((s) => {
      const map = new Map(s.typingByConversation);
      const cur = map.get(conversationId) ?? { userIds: new Set<string>(), expiresAt: 0 };
      const nextSet = new Set(cur.userIds);
      if (isTyping) nextSet.add(userId);
      else nextSet.delete(userId);
      map.set(conversationId, { userIds: nextSet, expiresAt: Date.now() + TYPING_TTL_MS });
      return { typingByConversation: map };
    }),

  cleanTyping: () =>
    set((s) => {
      const now = Date.now();
      let changed = false;
      const map = new Map(s.typingByConversation);
      for (const [k, v] of map) {
        if (v.expiresAt <= now && v.userIds.size > 0) {
          map.set(k, { userIds: new Set(), expiresAt: now + TYPING_TTL_MS });
          changed = true;
        } else if (v.expiresAt <= now && v.userIds.size === 0) {
          map.delete(k);
          changed = true;
        }
      }
      return changed ? { typingByConversation: map } : s;
    }),

  setPresence: (userId, online, lastSeen = null) =>
    set((s) => {
      const map = new Map(s.presenceByUser);
      map.set(userId, { online, lastSeen });
      return { presenceByUser: map };
    }),

  upsertPoll: (poll) =>
    set((s) => {
      const map = new Map(s.pollsById);
      map.set(poll.id, poll);
      return { pollsById: map };
    }),

  queueReadMarker: (conversationId, messageId, createdAt) => {
    const prev = get().pendingReadMarkers.get(conversationId) ?? null;
    // Only bump forward — older messages should not "un-read" newer ones.
    // Compare on createdAt because message ids are random hex (see
    // apps/api/src/lib/crypto.ts newId) and have no temporal order.
    if (prev && prev.createdAt >= createdAt) return null;
    const next = new Map(get().pendingReadMarkers);
    next.set(conversationId, { id: messageId, createdAt });
    set({ pendingReadMarkers: next });
    return messageId;
  },

  consumeReadMarker: (conversationId) => {
    const pending = get().pendingReadMarkers.get(conversationId) ?? null;
    if (!pending) return null;
    const next = new Map(get().pendingReadMarkers);
    next.delete(conversationId);
    set({ pendingReadMarkers: next });
    return pending.id;
  },

  setWsStatus: (status) =>
    set(() => {
      // F-001: on disconnect or reconnect failure, clear all typing
      // indicators so the recipient doesn't keep seeing "Alice is
      // typing…" while Alice is actually offline. The server-side
      // 6-second TTL will eventually expire them, but we'd rather not
      // show a stale indicator for that long.
      if (status === 'closed' || status === 'error' || status === 'idle') {
        return { wsStatus: status, typingByConversation: new Map() };
      }
      return { wsStatus: status };
    }),

  setReactions: (messageId, reactions) =>
    set((s) => {
      const map = new Map(s.reactionsByMessage);
      map.set(messageId, reactions);
      return { reactionsByMessage: map };
    }),

  setNotificationPref: (conversationId, level) =>
    set((s) => {
      const map = new Map(s.notificationPrefs);
      map.set(conversationId, level);
      return { notificationPrefs: map };
    }),

  getConversationPref: (conversationId) =>
    get().notificationPrefs.get(conversationId) ?? 'all',

  setDraft: (conversationId, content, replyToId) =>
    set((s) => ({
      draftsByConversation: {
        ...s.draftsByConversation,
        [conversationId]: { content, replyToId },
      },
    })),

  clearDraft: (conversationId) =>
    set((s) => {
      if (!(conversationId in s.draftsByConversation)) return s;
      const next = { ...s.draftsByConversation };
      delete next[conversationId];
      return { draftsByConversation: next };
    }),

  setReadBy: (messageId, readBy) =>
    set((s) => {
      const map = new Map(s.readByByMessage);
      map.set(messageId, readBy);
      return { readByByMessage: map };
    }),

  reset: () =>
    set({
      conversations: [],
      currentConversationId: null,
      currentUserId: null,
      messagesByConversation: new Map(),
      wsPendingMessages: new Map(),
      membersByConversation: new Map(),
      typingByConversation: new Map(),
      presenceByUser: new Map(),
      pollsById: new Map(),
      pendingReadMarkers: new Map(),
      wsStatus: 'idle',
      reactionsByMessage: new Map(),
      notificationPrefs: new Map(),
      draftsByConversation: {},
      readByByMessage: new Map(),
    }),
  }))
);

/* Convenience selectors */
export function selectCurrentConversation(s: { conversations: Conversation[]; currentConversationId: string | null }) {
  return s.conversations.find((c) => c.id === s.currentConversationId) ?? null;
}

export function selectMessagesFor(s: { messagesByConversation: Map<string, Message[]> }, id: string | null): Message[] {
  if (!id) return [];
  return s.messagesByConversation.get(id) ?? [];
}

export function selectTypingFor(s: { typingByConversation: Map<string, TypingState> }, id: string | null, currentUserId: string | null) {
  if (!id) return [] as User[];
  const t = s.typingByConversation.get(id);
  if (!t) return [];
  return Array.from(t.userIds)
    .filter((u) => u !== currentUserId)
    .map((u) => ({ id: u, username: '', displayName: '', avatarUrl: null, lastSeen: null } as User));
}
